// frontend/src/components/AnalyzeProject.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  Skeleton,
  Tabs,
  Tab
} from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SummarizeIcon from '@mui/icons-material/Summarize';
import api from '../services/api';

function AnalyzeProject({ projectId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [availableLanguages, setAvailableLanguages] = useState([]);

  const LANGUAGE_NAMES = {
    'en': 'English',
    'kz': 'Қазақша',
    'ru': 'Русский'
  };

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await api.get(`/projects/${projectId}/translations`);
        const languages = Object.keys(response.data);
        setAvailableLanguages(['en', ...languages.filter(lang => lang !== 'en')]);
      } catch (err) {
        console.error('Failed to fetch languages:', err);
      }
    };

    fetchLanguages();
  }, [projectId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`/projects/${projectId}/analyze`, {
          params: { language: selectedLanguage }
        });
        
        if (response.data.analyzed_count === 0) {
          setError('No analyzed responses available. Please mark some responses as "Analyzed" to generate insights.');
          return;
        }

        setData(response.data);
      } catch (err) {
        setError('Failed to load analysis data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [projectId, selectedLanguage]);

  const LanguageTabs = () => (
    <Tabs
      value={selectedLanguage}
      onChange={(e, newValue) => setSelectedLanguage(newValue)}
      sx={{ mb: 3 }}
    >
      {availableLanguages.map(lang => (
        <Tab
          key={lang}
          value={lang}
          label={LANGUAGE_NAMES[lang]}
          sx={{
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 'bold',
            },
          }}
        />
      ))}
    </Tabs>
  );

  const formatInsights = (insightsText) => {
    if (!insightsText) return { positive: [], negative: [] };

    const positiveMatch = insightsText.match(/Positive Insights:([\s\S]*?)(?=Areas for Improvement:|$)/i);
    const negativeMatch = insightsText.match(/Areas for Improvement:([\s\S]*?)$/i);

    const parseSection = (text) => {
      if (!text) return [];
      return text
        .split('\n')
        .filter(line => line.trim().startsWith('-'))
        .map(line => {
          const insight = line.trim().substring(1).trim();
          return insight.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        });
    };

    return {
      positive: positiveMatch ? parseSection(positiveMatch[1]) : [],
      negative: negativeMatch ? parseSection(negativeMatch[1]) : []
    };
  };

  if (isLoading) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        {/* Language Tabs Skeleton */}
        {availableLanguages.length > 1 && (
          <Box sx={{ mb: 3 }}>
            <Skeleton variant="rectangular" width={300} height={48} sx={{ borderRadius: 1 }} />
          </Box>
        )}

        {/* Summary Skeleton */}
        <Box sx={{ mb: 3 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
              <Skeleton width={120} height={28} />
            </Box>
            <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
            <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
            <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="80%" />
          </Paper>
        </Box>

        {/* Insights Skeletons */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* Positive Insights Skeleton */}
          <Box sx={{ flex: 1 }}>
            <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                <Skeleton width={140} height={28} />
              </Box>
              <List>
                {[1, 2, 3].map((_, index) => (
                  <React.Fragment key={`skeleton-positive-${index}`}>
                    {index > 0 && <Divider sx={{ my: 1 }} />}
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Skeleton variant="circular" width={6} height={6} />
                      </ListItemIcon>
                      <ListItemText>
                        <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                      </ListItemText>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Box>

          {/* Areas for Improvement Skeleton */}
          <Box sx={{ flex: 1 }}>
            <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                <Skeleton width={140} height={28} />
              </Box>
              <List>
                {[1, 2, 3].map((_, index) => (
                  <React.Fragment key={`skeleton-negative-${index}`}>
                    {index > 0 && <Divider sx={{ my: 1 }} />}
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Skeleton variant="circular" width={6} height={6} />
                      </ListItemIcon>
                      <ListItemText>
                        <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                      </ListItemText>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Box>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No analysis data available.</Alert>
      </Box>
    );
  }

  const { positive, negative } = formatInsights(data.insights);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {availableLanguages.length > 1 && <LanguageTabs />}
      
      {/* Summary Card */}
      <Box sx={{ mb: 3 }}>
        <Paper elevation={1} sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <SummarizeIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              Summary
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            {data.summary}
          </Typography>
        </Paper>
      </Box>

      {/* Insights Cards */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Positive Insights */}
        <Box sx={{ flex: 1 }}>
          <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
            <Box display="flex" alignItems="center" mb={2}>
              <LightbulbIcon sx={{ mr: 1, color: 'success.main' }} />
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                Positive Insights
              </Typography>
            </Box>
            <List>
              {positive.map((insight, index) => (
                <React.Fragment key={`positive-${index}`}>
                  {index > 0 && <Divider sx={{ my: 1 }} />}
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
                    </ListItemIcon>
                    <ListItemText>
                      <div dangerouslySetInnerHTML={{ __html: insight }} />
                    </ListItemText>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>

        {/* Areas for Improvement */}
        <Box sx={{ flex: 1 }}>
          <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
            <Box display="flex" alignItems="center" mb={2}>
              <LightbulbIcon sx={{ mr: 1, color: 'error.main' }} />
              <Typography variant="h6" color="error.main" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                Areas for Improvement
              </Typography>
            </Box>
            <List>
              {negative.map((insight, index) => (
                <React.Fragment key={`negative-${index}`}>
                  {index > 0 && <Divider sx={{ my: 1 }} />}
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'error.main' }} />
                    </ListItemIcon>
                    <ListItemText>
                      <div dangerouslySetInnerHTML={{ __html: insight }} />
                    </ListItemText>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
}

export default AnalyzeProject;
